import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Right from "./Right";
import {
  Autocomplete,
  TextField,
  Chip,
  Slider,
  Typography,
} from "@mui/material";
import { maritalStatusList } from "../../json/maritalStatusList";
import { dietList } from "../../json/dietList";
import { religionList } from "../../json/religionList";
import { motherTongueList } from "../../json/motherTongueList";
import { communityList } from "../../json/communityList";
import { qualificationList } from "../../json/qualificationList";
import { professionList } from "../../json/professionList";
import { workingInList } from "../../json/workingInList";
import { stateCityList } from "../../json/stateCityList";

export default function Filter() {
  const navigate = useNavigate();

  const defaultFilter = {
    ageRange: [21, 40],
    heightRange: [52, 80],
    maritalStatus: ["Open to All"],
    diet: ["Open to All"],
    religion: ["Open to All"],
    community: ["Open to All"],
    motherTongue: ["Open to All"],
    state: ["Open to All"],
    qualification: ["Open to All"],
    profession: ["Open to All"],
    workingIn: ["Open to All"],
  };

  const [filter, setFilter] = useState(defaultFilter);

  // Load filter from localStorage if exists
  useEffect(() => {
    const stored = localStorage.getItem("filter");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.filter) setFilter({ ...defaultFilter, ...parsed.filter });
    }
  }, []);

  const stateOptions = Array.from(new Set(stateCityList.map((i) => i.State)));

  const inchesToFeet = (inches) => {
    const feet = Math.floor(inches / 12);
    const inch = inches % 12;
    return `${feet}'${inch}"`;
  };

  const filteredCommunityOptions = communityList
    .map((i) => i.Name)
    .filter((c) => !filter.religion.some((r) => c.startsWith(r + "-")));

  const removeOpenToAll = (values) => {
    const filtered = values.filter((v) => v !== "Open to All");
    return filtered.length ? filtered : ["Open to All"];
  };

  // Function to save filter in localStorage before navigating
  const navigateWithFilter = () => {
    localStorage.setItem("filter", JSON.stringify({ filter }));
    navigate("/matches", { state: { filter } });
  };

  const resetFilter = () => {
    setFilter(defaultFilter);
    localStorage.setItem("filter", JSON.stringify({ filter: defaultFilter }));
    navigate("/matches", { state: { filter: defaultFilter } });
  };

  return (
    <>
      <Header />
      <section>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-lg-8 col-12 mb-5">
              <div className="d-flex align-items-center mb-3">
                <Link
                  onClick={() => navigate(-1)}
                  className="icon-shape me-3 icon-lg bg-light-red rounded-circle"
                >
                  <i className="bi bi-chevron-left fs-3 text-primary"></i>
                </Link>
                <div>
                  <h3 className="fw-medium ls-sm text-dark lh-1 fs-4 m-0">
                    Filter Profile
                  </h3>
                  <p className="lh-sm fw-normal m-0">
                    View Filtered Profiles / Matches
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-4 p-5">
                <div className="row g-3">
                  {/* Age & Height Sliders */}
                  <div className="col-lg-6 col-12 pe-lg-3">
                    <Typography gutterBottom>
                      <p className="fs-5 lh-sm text-dark">
                        Age Range
                        <br /> {filter.ageRange[0]} - {filter.ageRange[1]} years
                      </p>
                    </Typography>
                    <Slider
                      value={filter.ageRange}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({ ...f, ageRange: newValue }))
                      }
                      valueLabelDisplay="auto"
                      min={18}
                      max={45}
                    />
                  </div>

                  <div className="col-lg-6 col-12 ps-lg-3">
                    <Typography gutterBottom>
                      <p className="fs-5 lh-sm text-dark">
                        Height Range
                        <br /> {inchesToFeet(filter.heightRange[0])} -{" "}
                        {inchesToFeet(filter.heightRange[1])}
                      </p>
                    </Typography>
                    <Slider
                      value={filter.heightRange}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({ ...f, heightRange: newValue }))
                      }
                      valueLabelDisplay="auto"
                      min={48}
                      max={84}
                    />
                  </div>

                  {/* Autocomplete Fields */}
                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={maritalStatusList.map((i) => i.Name)}
                      value={filter.maritalStatus}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          maritalStatus: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Marital Status" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={dietList.map((i) => i.Name)}
                      value={filter.diet}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          diet: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Diet" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={religionList.map((i) => i.Name)}
                      value={filter.religion}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          religion: removeOpenToAll(newValue),
                          community: [],
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Religion" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={filteredCommunityOptions}
                      value={filter.community}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          community: removeOpenToAll(newValue),
                        }))
                      }
                      getOptionLabel={(option) => option.replace(/^[^-]+-/, "")}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option.replace(/^[^-]+-/, "")}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Community" size="small" />
                      )}
                    />
                  </div>

                  {/* Remaining Autocomplete Fields */}
                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={motherTongueList.map((i) => i.Name)}
                      value={filter.motherTongue}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          motherTongue: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Mother Tongue" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={stateOptions}
                      value={filter.state}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          state: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="State" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={qualificationList.map((i) => i.Name)}
                      value={filter.qualification}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          qualification: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Qualification" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={workingInList.map((i) => i.Name)}
                      value={filter.workingIn}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          workingIn: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Working In" size="small" />
                      )}
                    />
                  </div>

                  <div className="col-lg-6 col-12">
                    <Autocomplete
                      multiple
                      options={professionList.map((i) => i.Name)}
                      value={filter.profession}
                      onChange={(e, newValue) =>
                        setFilter((f) => ({
                          ...f,
                          profession: removeOpenToAll(newValue),
                        }))
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option}
                            label={option}
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Profession" size="small" />
                      )}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <span
                    className="btn btn-danger rounded-pill px-5"
                    onClick={navigateWithFilter}
                  >
                    Show Filtered Profiles
                  </span>
                  <span
                    className="btn btn-dark rounded-pill px-3 ms-1"
                    onClick={resetFilter}
                  >
                    <i className="bi bi-x-lg"></i>
                  </span>
                </div>
              </div>
            </div>

            <Right />
          </div>
        </div>
      </section>
    </>
  );
}
